import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { CardStatementTransaction } from "../../types/card-statement.types";
import { formatTransactionAmount } from "../../utils/cardTransactionDetailHelpers";
import { StatementMovementRow } from "./StatementMovementRow";

export interface StatementMovementsTableProps {
  transactions: CardStatementTransaction[];
  /** Shown once, on the first page. */
  showHeading: boolean;
  /** The net-total band closes the document on the last page. */
  netTotal: number | null;
  currency: string;
}

export const StatementMovementsTable = ({
  transactions,
  showHeading,
  netTotal,
  currency,
}: StatementMovementsTableProps) => {
  const { t, activeLanguage } = useTranslations("cards");

  return (
    <section className={cn("px-10", showHeading ? "pb-4" : "pb-4 pt-5")}>
      {showHeading && (
        <h2 className="mb-2 text-[11px] font-semibold text-foreground">{t("statement_section_movements")}</h2>
      )}

      <table className="w-full table-fixed border-collapse">
        <colgroup>
          <col style={{ width: "78px" }} />
          <col style={{ width: "64px" }} />
          <col />
          <col style={{ width: "96px" }} />
          <col style={{ width: "70px" }} />
          <col style={{ width: "96px" }} />
        </colgroup>
        <thead>
          <tr className="border-b border-foreground/25 text-left text-[8.5px] uppercase tracking-wider text-muted-foreground">
            <th className="pb-1.5 pr-2 font-semibold">{t("statement_table_date")}</th>
            <th className="pb-1.5 pr-2 font-semibold">{t("statement_table_reference")}</th>
            <th className="pb-1.5 pr-2 font-semibold">{t("statement_table_merchant")}</th>
            <th className="pb-1.5 pr-2 font-semibold">{t("statement_table_category")}</th>
            <th className="pb-1.5 pr-2 font-semibold">{t("statement_table_type")}</th>
            <th className="pb-1.5 pl-2 text-right font-semibold">{t("statement_table_amount")}</th>
          </tr>
        </thead>
        <tbody>
          {transactions.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-8 text-center text-[10px] text-muted-foreground">
                {t("statement_table_empty")}
              </td>
            </tr>
          ) : (
            transactions.map((tx) => <StatementMovementRow key={tx.id} transaction={tx} />)
          )}
        </tbody>
        {netTotal !== null && (
          <tfoot>
            <tr className="border-t-2 border-foreground">
              <td
                colSpan={5}
                className="pr-2 pt-2 text-[8.5px] font-semibold uppercase tracking-wider text-foreground"
              >
                {t("statement_table_total_period")}
              </td>
              <td className="pl-2 pt-2 text-right font-mono text-[11px] font-semibold tabular-nums text-foreground">
                {netTotal >= 0 ? "+" : "−"}
                {formatTransactionAmount(Math.abs(netTotal), currency, activeLanguage)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </section>
  );
};
